import { MapPin, Plane, Train } from 'lucide-react';
import LocationImg from '../../assets/Background/location_bg.png';
import FlightImg from '../../assets/Background/flight_bg.png';
import TrainImg from '../../assets/Background/train_bg.png';

export const TRACKING_TABS = [
  { id: 'live-location', label: 'Live Location', icon: MapPin },
  { id: 'flight-tracking', label: 'Flight Tracking', icon: Plane },
  { id: 'train-tracking', label: 'Train Tracking', icon: Train }
];

export const TRACKING_BANNERS = {
  'live-location': {
    title: 'Live Location Tracking',
    description:
      'Monitor real-time GPS routes of active tour guests and send push notification alerts to coordinate tours seamlessly.',
    image: LocationImg
  },
  'flight-tracking': {
    title: 'Live Flight Tracking',
    description:
      'Real-time flight status updates for you and your customers. Track any flight instantly with PNR or Flight Number.',
    image: FlightImg
  },
  'train-tracking': {
    title: 'Live Train Tracking',
    description:
      'Track live train status, PNR status, and running information instantly. Keep your customers informed.',
    image: TrainImg
  }
};

export const tabVariants = {
  hidden: { opacity: 0, y: 12 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.25, ease: 'easeOut' } },
  exit: { opacity: 0, y: -8, transition: { duration: 0.15, ease: 'easeIn' } }
};

export const DAYS_FILTER_OPTIONS = [
  { value: 1, label: 'Next 24 Hours' },
  { value: 3, label: 'Next 3 Days' },
  { value: 5, label: 'Next 5 Days' },
  { value: 7, label: 'Next 1 Week' },
  { value: 14, label: 'Next 2 Weeks' }
];

export const FLIGHT_STATUS_CONFIG = {
  landed: {
    label: 'Landed',
    flightPhase: 'Arrived · Parked at Gate',
    badgeClass: 'bg-emerald-50 text-emerald-700 border-emerald-200/80',
    dotClass: 'bg-emerald-500',
    pulse: false
  },
  active: {
    label: 'In Flight',
    flightPhase: 'En Route · Cruising Altitude',
    badgeClass: 'bg-blue-50 text-blue-700 border-blue-200/80',
    dotClass: 'bg-blue-500',
    pulse: true
  },
  scheduled: {
    label: 'Scheduled',
    flightPhase: 'Flight Plan Filed · On Schedule',
    badgeClass: 'bg-slate-50 text-slate-700 border-slate-200/80',
    dotClass: 'bg-slate-400',
    pulse: false
  },
  cancelled: {
    label: 'Cancelled',
    flightPhase: 'Flight Service Cancelled',
    badgeClass: 'bg-rose-50 text-rose-700 border-rose-200/80',
    dotClass: 'bg-rose-500',
    pulse: false
  }
};

export const mapContainerStyle = {
  width: '100%',
  height: '100%'
};

export const premiumMapStyle = [
  { elementType: 'geometry', stylers: [{ color: '#f5f5f5' }] },
  { elementType: 'labels.icon', stylers: [{ visibility: 'on' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#616161' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#f5f5f5' }] },
  { featureType: 'administrative.land_parcel', elementType: 'labels.text.fill', stylers: [{ color: '#bdbdbd' }] },
  { featureType: 'poi', elementType: 'geometry', stylers: [{ color: '#eeeeee' }] },
  { featureType: 'poi', elementType: 'labels.text.fill', stylers: [{ color: '#757575' }] },
  { featureType: 'poi.park', elementType: 'geometry', stylers: [{ color: '#e5e5e5' }] },
  { featureType: 'poi.park', elementType: 'labels.text.fill', stylers: [{ color: '#9e9e9e' }] },
  { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#ffffff' }] },
  { featureType: 'road.arterial', elementType: 'labels.text.fill', stylers: [{ color: '#757575' }] },
  { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#dadada' }] },
  { featureType: 'road.highway', elementType: 'labels.text.fill', stylers: [{ color: '#616161' }] },
  { featureType: 'road.local', elementType: 'labels.text.fill', stylers: [{ color: '#9e9e9e' }] },
  { featureType: 'transit.line', elementType: 'geometry', stylers: [{ color: '#e5e5e5' }] },
  { featureType: 'transit.station', elementType: 'geometry', stylers: [{ color: '#eeeeee' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#c9e3e3' }] },
  { featureType: 'water', elementType: 'labels.text.fill', stylers: [{ color: '#067978' }] },
  {
    featureType: 'road',
    elementType: 'geometry.stroke',
    stylers: [{ color: '#dcdcdc' }]
  }
];

export const markerIcon = {
  path: 'M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z',
  fillColor: '#067978',
  fillOpacity: 1,
  strokeWeight: 2,
  strokeColor: '#ffffff',
  scale: 2,
  anchor: { x: 12, y: 22 }
};

export const IATA_CITIES = {
  BOM: 'Mumbai',
  DEL: 'New Delhi',
  BLR: 'Bengaluru',
  MAA: 'Chennai',
  CCU: 'Kolkata',
  HYD: 'Hyderabad',
  GOI: 'Goa (Dabolim)',
  GOX: 'Goa (Mopa)',
  AMD: 'Ahmedabad',
  COK: 'Kochi',
  JAI: 'Jaipur',
  PNQ: 'Pune',
  LKO: 'Lucknow',
  GAU: 'Guwahati',
  SXR: 'Srinagar',
  IXC: 'Chandigarh',
  ATQ: 'Amritsar',
  BBI: 'Bhubaneswar',
  PAT: 'Patna',
  TRV: 'Thiruvananthapuram',
  IXB: 'Bagdogra',
  UDR: 'Udaipur',
  VNS: 'Varanasi',
  IXZ: 'Port Blair',
  DXB: 'Dubai',
  SIN: 'Singapore',
  BKK: 'Bangkok',
  DOH: 'Doha',
  AUH: 'Abu Dhabi',
  KUL: 'Kuala Lumpur',
  LHR: 'London',
  JFK: 'New York'
};

export const AIRCRAFT_NAMES = {
  A320: 'Airbus A320',
  A321: 'Airbus A321',
  A319: 'Airbus A319',
  A359: 'Airbus A350-900',
  A35K: 'Airbus A350-1000',
  A332: 'Airbus A330-200',
  A333: 'Airbus A330-300',
  B737: 'Boeing 737',
  B738: 'Boeing 737-800',
  B739: 'Boeing 737-900',
  B38M: 'Boeing 737 MAX 8',
  B772: 'Boeing 777-200',
  B77W: 'Boeing 777-300ER',
  B788: 'Boeing 787-8 Dreamliner',
  B789: 'Boeing 787-9 Dreamliner',
  AT72: 'ATR 72-600',
  AT76: 'ATR 72-600',
  DH8D: 'Dash 8-400'
};

export const liveColumns = [
  { label: 'Guest & ID', span: 'col-span-3' },
  { label: 'Destination', span: 'col-span-3' },
  { label: 'Status', span: 'col-span-3' },
  { label: 'Actions', span: 'col-span-3', align: 'right' }
];

export const liveGhostRows = [
  {
    id: 'live-1',
    opacity: 'opacity-40',
    guest: 'Example Traveller',
    bookingId: 'l4m9k',
    destination: 'Manali, Himachal Pradesh',
    status: 'onTrip',
    statusClass: 'bg-emerald-50 text-emerald-600 border-emerald-100',
    devices: '2 Devices Active'
  },
  {
    id: 'live-2',
    opacity: 'opacity-30',
    guest: 'Example Traveller',
    bookingId: 's2x7q',
    destination: 'Goa (North & South)',
    status: 'installed',
    statusClass: 'bg-indigo-50 text-indigo-600 border-indigo-100',
    devices: '1 Device Logged In'
  },
  {
    id: 'live-3',
    opacity: 'opacity-20',
    guest: 'Example Traveller',
    bookingId: 'd5t1w',
    destination: 'Jaipur, Rajasthan',
    status: 'onTrip',
    statusClass: 'bg-emerald-50 text-emerald-600 border-emerald-100',
    devices: '2 Devices Active'
  },
  {
    id: 'live-4',
    opacity: 'opacity-10',
    guest: 'Example Traveller',
    bookingId: 'k8h3p',
    destination: 'Munnar, Kerala',
    status: 'upcoming',
    statusClass: 'bg-slate-100 text-slate-600 border-slate-200',
    devices: 'App Code Shared'
  }
];

export const flightColumns = [
  { label: 'Guest & ID', span: 'col-span-3' },
  { label: 'Flight Details', span: 'col-span-3' },
  { label: 'Route & Time', span: 'col-span-3' },
  { label: 'Timeline', span: 'col-span-3', align: 'right' }
];

export const flightGhostRows = [
  {
    id: 'fl-1',
    opacity: 'opacity-40',
    guest: 'Example Traveller',
    bookingId: 'f8k2m',
    flightNumber: '6E-204',
    pnr: 'QW89KL',
    from: 'DEL (Delhi)',
    to: 'GOI (Goa)',
    departureTime: '09:45',
    timeline: 'In 2 days',
    statusClass: 'bg-emerald-50 text-emerald-600 border-emerald-100'
  },
  {
    id: 'fl-2',
    opacity: 'opacity-30',
    guest: 'Example Traveller',
    bookingId: 'x9p1q',
    flightNumber: 'AI-805',
    pnr: 'TR445M',
    from: 'BOM (Mumbai)',
    to: 'BLR (Bengaluru)',
    departureTime: '14:30',
    timeline: 'Tomorrow',
    statusClass: 'bg-amber-50 text-amber-600 border-amber-100'
  },
  {
    id: 'fl-3',
    opacity: 'opacity-20',
    guest: 'Example Traveller',
    bookingId: 'a4d7v',
    flightNumber: 'UK-993',
    pnr: 'LK912P',
    from: 'DEL (Delhi)',
    to: 'CCU (Kolkata)',
    departureTime: '18:00',
    timeline: 'In 3 days',
    statusClass: 'bg-teal-50 text-[#067978] border-teal-100'
  },
  {
    id: 'fl-4',
    opacity: 'opacity-10',
    guest: 'Example Traveller',
    bookingId: 'm2k5w',
    flightNumber: 'SG-192',
    pnr: 'ZX771Q',
    from: 'JAI (Jaipur)',
    to: 'BOM (Mumbai)',
    departureTime: '11:20',
    timeline: 'In 5 days',
    statusClass: 'bg-slate-100 text-slate-600 border-slate-200'
  }
];

export const trainColumns = [
  { label: 'Guest & ID', span: 'col-span-3' },
  { label: 'Train Details', span: 'col-span-3' },
  { label: 'Route & Time', span: 'col-span-3' },
  { label: 'Timeline', span: 'col-span-3', align: 'right' }
];

export const trainGhostRows = [
  {
    id: 'tr-1',
    opacity: 'opacity-40',
    guest: 'Example Traveller',
    bookingId: 't7r2b',
    trainNumber: '20608',
    trainName: 'Vande Bharat Express',
    from: 'MAS (Chennai)',
    to: 'MYS (Mysuru)',
    departureTime: '05:50',
    timeline: 'Tomorrow',
    statusClass: 'bg-emerald-50 text-emerald-600 border-emerald-100'
  },
  {
    id: 'tr-2',
    opacity: 'opacity-30',
    guest: 'Example Traveller',
    bookingId: 'w3k8p',
    trainNumber: '12431',
    trainName: 'Rajdhani Express',
    from: 'TVC (Trivandrum)',
    to: 'NZM (Nizamuddin)',
    departureTime: '19:15',
    timeline: 'In 2 days',
    statusClass: 'bg-teal-50 text-[#067978] border-teal-100'
  },
  {
    id: 'tr-3',
    opacity: 'opacity-20',
    guest: 'Example Traveller',
    bookingId: 'q5y1m',
    trainNumber: '12002',
    trainName: 'Bhopal Shatabdi',
    from: 'NDLS (New Delhi)',
    to: 'BPL (Bhopal)',
    departureTime: '06:00',
    timeline: 'In 4 days',
    statusClass: 'bg-amber-50 text-amber-600 border-amber-100'
  },
  {
    id: 'tr-4',
    opacity: 'opacity-10',
    guest: 'Example Traveller',
    bookingId: 'b8v3c',
    trainNumber: '82902',
    trainName: 'Tejas Express',
    from: 'ADI (Ahmedabad)',
    to: 'MMCT (Mumbai Central)',
    departureTime: '06:40',
    timeline: 'In 5 days',
    statusClass: 'bg-slate-100 text-slate-600 border-slate-200'
  }
];
